import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { ControllerRenderProps } from "react-hook-form";
import {
  ApprovalDetailInformationEntity,
  ApprovalEntity,
  ApprovalTemplateEntity,
  ListOfApprovalTemporaryStorageEntity
} from "../../../generated/graphql";
import routes from "../../../routes";
import Button from "../../globalComponents/Button";
import StyleInput from "../../inputs/style-input";
import StyleTextarea from "../../inputs/style-textarea";
import { ApprovalType } from "../approval-detail-popup";
import { IEmployeeInformation } from "../approval-process/list-of-selected-approval-user";
import { IApprovalFormFieldValue } from "./types";
import {
  Container,
  Content,
  Description,
  DisplayUsers,
  Label,
  MainSection,
  Subscription
} from "./styles";
import ApproverList from "./ApproverList";
import ApprovalFormTitle from "./ApprovalFormTitle";
import { useApprovalForm } from "./hooks/useApprovalForm";
import ApprovalDetailPopupStore from "../approval-detail-popup-store.ctx";
import TimeInfoDisplay from "./components/TimeInfoDisplay";
import WorkingTemplateSelect from "./components/WorkingTemplateSelect";
import DateTimeFields from "./components/DateTimeFields";
import BasicInfoDisplay from "./components/BasicInfoDisplay";
import FileUploadWithAxios from "../../upload-files/FileUploadWithAxios";
import { UploadUserInfo } from "../../upload-files/types";
import { useFileUpload } from "../../upload-files/useFileUpload";

interface IProps {
  approvalTemporaryStorageData?: ListOfApprovalTemporaryStorageEntity;
  data?: ApprovalEntity;
  approvalType?: ApprovalType;
  isRequest?: boolean;
  template?: ApprovalTemplateEntity;
  handleIsReceiveAndReference?: (value: boolean) => void;
  listOfUser?: IEmployeeInformation[];
  listOfReferenceUserName: string[];
  listOfReceiveUserName: string[];
  listOfReceiveDepartmentName: string[];
  listOfReferenceDepartmentName: string[];
  title: ControllerRenderProps<IApprovalFormFieldValue, "title">;
  startDate: ControllerRenderProps<IApprovalFormFieldValue, "startDate">;
  endDate: ControllerRenderProps<IApprovalFormFieldValue, "endDate">;
  startTime: ControllerRenderProps<IApprovalFormFieldValue, "startTime">;
  endTime: ControllerRenderProps<IApprovalFormFieldValue, "endTime">;
  description: ControllerRenderProps<IApprovalFormFieldValue, "description">;
  approvalDetailInformation?: ApprovalDetailInformationEntity;
}

// [{"filename":"GT2024-11940-검사성적서 (55).pdf","filesize":222884,"link_address":"http://192.168.129.33:37011/10524/GT2024-11940-검사성적서 (55).pdf"}]

export interface IAttachment {
  filename: string;
  filesize: number;
  link_address: string;
}

export interface IApprovalDetailMessage {
  type: ApprovalType;
  data: ApprovalEntity;
}

type NumOrStr = string | number;
interface Option {
  value: NumOrStr;
  name?: string;
}

function ApprovalForm({
  data,
  isRequest,
  template,
  handleIsReceiveAndReference,
  listOfReceiveUserName,
  listOfReferenceUserName,
  listOfReceiveDepartmentName,
  listOfReferenceDepartmentName,
  title,
  startDate,
  endDate,
  startTime,
  endTime,
  description,
  approvalDetailInformation,
  approvalTemporaryStorageData,
  listOfUser = []
}: IProps) {
  const {
    formName,
    user,
    isActiveTime,
    annualInfo,
    approvalRemainingTimeInfo,
    departmentName,
    formTemplateIdx,
    isActiveWorkType,
    workingTemplateOptions
  } = useApprovalForm({
    data,
    isRequest,
    template,
    listOfUser,
    startDate,
    approvalDetailInformation,
    approvalTemporaryStorageData
  });

  const [selectedWorkingTemplateIndex, setSelectedWorkingTemplateIndex] =
    useState<number>(0);

  const { getWebdavAddress } = useFileUpload();
  const [isWebdavAddressExists, setIsWebdavAddressExists] =
    useState<boolean>(false);

  useEffect(() => {
    getWebdavAddress().then(setIsWebdavAddressExists);
  }, [getWebdavAddress]);

  const attachmentsArray = useMemo<IAttachment[]>(() => {
    return approvalDetailInformation?.attachedFileJson
      ? JSON.parse(approvalDetailInformation.attachedFileJson)
      : [];
  }, [approvalDetailInformation?.attachedFileJson]);

  const store = useContext(ApprovalDetailPopupStore);

  const isChangeWorkingTimeForm = useMemo(() => {
    // 출퇴근 시간 조정 폼 고정 번호
    const WORKING_TIME_FORM_IDX = 11;
    return store?.formIdx === WORKING_TIME_FORM_IDX;
  }, [store?.formIdx]);

  const handleFilesChange = useCallback(
    (files: FileList | null) => {
      const attachedFiles = Array.from(files ?? []).map(file => ({
        name: file.name,
        size: file.size
      }));
      store?.setAttachedFiles(attachedFiles);
    },
    [store?.setAttachedFiles]
  );

  const [uuid] = useState(() => crypto.randomUUID().slice(0, 8));
  return (
    <Container>
      <ApproverList
        data={data}
        isRequest={isRequest}
        template={template}
        listOfUser={listOfUser}
        startDate={startDate}
        approvalDetailInformation={approvalDetailInformation}
        approvalTemporaryStorageData={approvalTemporaryStorageData}
      />
      <ApprovalFormTitle
        template={template}
        approvalDetailInformation={approvalDetailInformation}
        formName={formName ?? ""}
      />
      <MainSection $annualInfo={annualInfo} $isRequest={isRequest}>
        <BasicInfoDisplay
          departmentName={departmentName}
          user={user}
          isRequest={isRequest}
          data={data}
          template={template}
          approvalDetailInformation={approvalDetailInformation}
        />

        <DateTimeFields
          isChangeWorkingTimeForm={isChangeWorkingTimeForm}
          isRequest={isRequest}
          isTemporaryStorage={!!approvalTemporaryStorageData}
          template={template}
          approvalDetailInformation={approvalDetailInformation}
          data={data}
          isActiveTime={isActiveTime}
          startDate={startDate}
          endDate={endDate}
          startTime={startTime}
          endTime={endTime}
        />

        <TimeInfoDisplay
          annualInfo={annualInfo}
          approvalRemainingTimeInfo={approvalRemainingTimeInfo}
        />

        <WorkingTemplateSelect
          isActiveWorkType={isActiveWorkType}
          workingTemplateOptions={workingTemplateOptions}
          selectedWorkingTemplateIndex={store?.workingTemplateIdx ?? 0}
          setWorkingTemplateIndex={value => store?.setWorkingTemplateIdx(value)}
        />

        <Content
          borderTop={isChangeWorkingTimeForm && isRequest}
          style={{ gridColumn: "span 3", gridRow: "" }}
        >
          <Label isSpacing>{`제   목`}</Label>
          <Subscription>
            {isRequest ? (
              <StyleInput
                {...title}
                minWidth="300px"
                maxWidth="100%"
                $width="100%"
              />
            ) : (
              <span>{title.value}</span>
            )}
          </Subscription>
        </Content>
        <Content
          style={{
            gridColumn: "span 3",
            gridRow: "span 5",
            minHeight: "150px"
          }}
        >
          {approvalDetailInformation?.extendDescription && (
            <Description>
              {approvalDetailInformation?.extendDescription}
            </Description>
          )}
          {isRequest && <StyleTextarea {...description} />}
        </Content>
        <Content style={{ gridColumn: "span 3", gridRow: "" }}>
          <Label isSpacing>{`수   신`}</Label>
          <Subscription>
            {isRequest && (
              <Button
                customMinHeight="20px"
                customMinWidth="50px"
                onClick={() => {
                  if (handleIsReceiveAndReference) {
                    handleIsReceiveAndReference(true);
                  }
                  window.open(
                    `${routes.pageRoutes.approvalProcess}${formTemplateIdx}`,
                    "",
                    "width=960, height=650"
                  );
                }}
              >
                수신선택
              </Button>
            )}
            <DisplayUsers>
              {listOfReceiveDepartmentName.map((item, index) => (
                <span key={`${index} ${item}`}>
                  {listOfReceiveDepartmentName.length - 1 === index
                    ? listOfReceiveUserName.length > 0
                      ? `${item},`
                      : item
                    : `${item},`}
                </span>
              ))}
              {listOfReceiveUserName.map((item, index) => (
                <span key={`${item} ${index}`}>
                  {listOfReceiveUserName.length - 1 === index
                    ? item
                    : `${item},`}
                </span>
              ))}
            </DisplayUsers>
          </Subscription>
        </Content>
        <Content style={{ gridColumn: "span 3", gridRow: "" }}>
          <Label isSpacing>{`참   조`}</Label>
          <Subscription>
            {isRequest && (
              <Button
                customMinHeight="20px"
                customMinWidth="50px"
                onClick={() => {
                  if (handleIsReceiveAndReference) {
                    handleIsReceiveAndReference(true);
                  }
                  window.open(
                    `${routes.pageRoutes.approvalProcess}${formTemplateIdx}`,
                    "",
                    "width=960, height=650"
                  );
                }}
              >
                참조선택
              </Button>
            )}
            <DisplayUsers>
              {listOfReferenceDepartmentName.map((item, index) => (
                <span key={`${index} ${item}`}>
                  {listOfReferenceDepartmentName.length - 1 === index
                    ? listOfReferenceUserName.length > 0
                      ? `${item},`
                      : item
                    : `${item},`}
                </span>
              ))}
              {listOfReferenceUserName.map((item, index) => (
                <span key={`${index} ${item}`}>
                  {listOfReferenceUserName.length - 1 === index
                    ? item
                    : `${item},`}
                </span>
              ))}
            </DisplayUsers>
          </Subscription>
        </Content>
        {isWebdavAddressExists && (
          <Content style={{ gridColumn: "span 3", gridRow: "" }}>
            <Label>첨부파일</Label>
            <Subscription>
              <FileUploadWithAxios
                mode="compact"
                uuid={uuid}
                onFilesChange={handleFilesChange}
                disabled={isRequest === false}
                attachments={attachmentsArray}
                setUserInfo={(userInfo: UploadUserInfo) => {
                  store?.setUploadUserInfo(userInfo);
                }}
              />
            </Subscription>
          </Content>
        )}
      </MainSection>
    </Container>
  );
}

export default ApprovalForm;
